import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AuthService } from '../../../../core/services/auth.service';
import { BranchService } from '../../../../core/services/branch.service';
import { CashShiftService } from '../../../../core/services/cash-shift.service';
import { UiService } from '../../../../core/services/ui.service';
import { cambioDeTurnoHabilitado } from '../../../../core/utils/cash-shift.util';
import { ShiftReceptionDialogComponent } from '../shift-reception-dialog/shift-reception-dialog';

/**
 * Bloqueo de la caja mientras hay un cambio de turno sin validar. Se monta una sola vez en el layout: cubre toda
 * la aplicación y obliga a la próxima vendedora a validar lo que recibe (o a anular el cambio) antes de seguir.
 * La base rechaza igual cualquier movimiento de caja mientras el traspaso está pendiente.
 */
@Component({
  selector: 'app-shift-handover-gate',
  imports: [DatePipe, FormsModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule, MatProgressSpinnerModule],
  templateUrl: './shift-handover-gate.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShiftHandoverGateComponent {
  // 1. Inyecciones
  private readonly authService = inject(AuthService);
  private readonly branchService = inject(BranchService);
  private readonly dialog = inject(MatDialog);
  private readonly uiService = inject(UiService);
  protected readonly cashShiftService = inject(CashShiftService);

  // 2. Estado
  readonly pendiente = this.cashShiftService.pendiente;
  readonly anulando = signal<boolean>(false);
  readonly motivo = signal<string>('');
  readonly procesando = signal<boolean>(false);

  // 4. Constructor
  constructor() {
    // Se consulta el traspaso pendiente al entrar y cada vez que cambia la sucursal activa o los interruptores.
    effect(() => {
      const habilitado = cambioDeTurnoHabilitado(this.authService.businessSettings(), this.branchService.workingBranch());
      const branchId = this.branchService.workingBranchId();
      if (habilitado && branchId) {
        void this.cashShiftService.cargarPendiente(branchId);
      } else {
        this.cashShiftService.pendiente.set(null);
      }
    });
  }

  // 5. Métodos
  /** Abre la ventana donde quien llega cuenta a ciegas y valida lo que recibe. */
  validar(): void {
    const pendiente = this.pendiente();
    if (!pendiente) return;
    this.dialog.open(ShiftReceptionDialogComponent, {
      width: '680px',
      maxWidth: '95vw',
      panelClass: 'clean-dialog',
      disableClose: true,
      data: { pendiente }
    });
  }

  /** Muestra el campo de motivo para anular el cambio de turno. */
  empezarAnulacion(): void {
    this.motivo.set('');
    this.anulando.set(true);
  }

  /** Vuelve a la pantalla de espera sin anular. */
  cancelarAnulacion(): void {
    this.anulando.set(false);
  }

  /** Anula el cambio de turno pendiente y libera la caja. Queda auditado con el motivo. */
  async confirmarAnulacion(): Promise<void> {
    const pendiente = this.pendiente();
    if (!pendiente || this.procesando()) return;

    this.procesando.set(true);
    try {
      const { error } = await this.cashShiftService.anular(pendiente.id, this.motivo().trim());
      if (error) {
        this.uiService.showError(error.message || 'No se pudo anular el cambio de turno.');
      } else {
        this.uiService.showSuccess('Cambio de turno anulado. La caja volvió a estar disponible.');
        this.anulando.set(false);
      }
      await this.cashShiftService.cargarPendiente(this.branchService.workingBranchId());
    } catch (e) {
      console.error('Error al anular el cambio de turno:', e);
      this.uiService.showError('Error inesperado al anular el cambio de turno.');
    } finally {
      this.procesando.set(false);
    }
  }
}
